import { HttpClient } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { IUser } from '../models/IAuth';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private platformId = inject(PLATFORM_ID);

  currentUser: IUser = {
    memberID: '',
    email: '',
    firstName: '',
    lastName: '',
    isDelegate: false,
    isSubscriber: false,
    hasSubscribed: false,
    isAdmin: false
  };

  loggedInStatus = false;

  // Observable auth state for templates (use async pipe)
  public loggedIn$ = new BehaviorSubject<boolean>(false);
  public isAdmin$ = new BehaviorSubject<boolean>(false);
  public isAssistant$ = new BehaviorSubject<boolean>(false);

  baseUrl: string = environment.baseUrl;
  private checkEmailURL = '/api/registration/check-email.php';

  constructor(private http: HttpClient) {

    if (isPlatformBrowser(this.platformId)) {


      const loggedInStatus = localStorage.getItem('loggedIn');
      if (loggedInStatus === 'true') {

        this.currentUser.email = localStorage.getItem('email') ?? '';
        this.currentUser.firstName = localStorage.getItem('firstName') ?? '';
        this.currentUser.lastName = localStorage.getItem('lastName') ?? '';
        this.currentUser.memberID = localStorage.getItem('memberID') ?? '';

        // Ensuring that booleans are actually true / false
        const isAdminString = localStorage.getItem('isAdmin');
        if (isAdminString !== null && isAdminString == 'true') {
          // This will correctly convert "true" to true, "false" to false
          this.currentUser.isAdmin = JSON.parse(isAdminString);
        } else {
          this.currentUser.isAdmin = false;
        }

        const isSubscriberString = localStorage.getItem('isSubscriber');
        if (isSubscriberString !== null && isSubscriberString == 'true') {
          this.currentUser.isSubscriber = JSON.parse(isSubscriberString);
        } else {
          this.currentUser.isSubscriber = false;
        }

        const hasSubscribedString = localStorage.getItem('hasSubscribed');
        if (hasSubscribedString !== null && hasSubscribedString == 'true') {
          this.currentUser.hasSubscribed = JSON.parse(hasSubscribedString);
        } else {
          this.currentUser.hasSubscribed = false;
        }

        const isDelegate = localStorage.getItem('isDelegate');
        if (isDelegate !== null && isDelegate == 'true') {
          // This will correctly convert "true" to true, "false" to false
          this.currentUser.isDelegate = JSON.parse(isDelegate);
        } else {
          this.currentUser.isDelegate = false;
        }

        // Restore Observable state so UI reflects login immediately
        this.loggedIn$.next(true);
        this.isAdmin$.next(this.currentUser.isAdmin);
        this.isAssistant$.next(this.currentUser.email == 'assistant@adsplat.me');

      } else {
        // User is not logged in, redirect to login page
        console.log('Auth data not loaded from storage:');
      }


    }
  }

  login(model: { email: string; password: string }): Observable<any> {

    if (isPlatformBrowser(this.platformId)) {

      this.clearUserInfo();

    }

    return this.http.post('/api/login/login.php', model).pipe(
      map((response: any) => {

        if (!response.member) {
          throw new Error('Invalid login response: missing member data');
        }
        const user = response.member as IUser;

        if (isPlatformBrowser(this.platformId)) {
          this.setUserInfo(user);
        }

        return response;
      })
    );
  }

  isLoggedIn(): boolean {

    if (isPlatformBrowser(this.platformId)) {
      const status = localStorage.getItem('loggedIn');
      return (status == 'true') ? true : false;
    }
    else
      return false;
  }

  isAdmin() {

    if (this.currentUser.isAdmin == true)
      return true;
    else
      return false;
  }

  isAssistant() {

    if (this.currentUser.email == "assistant@adsplat.me")
      return true;
    else
      return false;
  }

  isSubscriber() {
    return this.currentUser.isSubscriber;
  }

  setIsDelegate(isDelegate: boolean): void {

    if (isPlatformBrowser(this.platformId)) {
      this.currentUser.isDelegate = Boolean(isDelegate);
      localStorage.setItem('isDelegate', JSON.stringify(isDelegate));
    }

  }

  setIsSubscriber(isSubscriber: boolean): void {

    if (isPlatformBrowser(this.platformId)) {
      this.currentUser.isSubscriber = Boolean(isSubscriber);
      localStorage.setItem('isSubscriber', JSON.stringify(isSubscriber));
    }

  }

  setHasSubscribed(hasSubscribed: boolean): void {

    if (isPlatformBrowser(this.platformId)) {
      this.currentUser.hasSubscribed = Boolean(hasSubscribed);
      localStorage.setItem('hasSubscribed', JSON.stringify(hasSubscribed));
    }

  }

  logout(): Observable<any> {
    // It's good practice to use POST for logout, as it's an action that changes state.
    // Ensure your backend's /api/logout endpoint is configured to handle POST requests.
    return this.http.post<any>('/api/logout/logout.php', {}) // Send an empty body for POST
      .pipe(
        tap(() => {

          //console.log('Logout successful on backend. Deleting localStorage flag.');

          if (isPlatformBrowser(this.platformId)) {

            this.clearUserInfo();

          }

        }),
        // finalize is useful if you have cleanup that needs to happen regardless
        // of success or error (e.g., stopping a loading spinner).
        // For localStorage, tap is sufficient for success.
        finalize(() => {
          //console.log('Logout observable completed (success or error).');
        })
      );

  }

  checkEmailExists(email: string): Observable<any> {
    return this.http.post<any>(this.checkEmailURL, { email });
  }

  setUserInfo(user: IUser) {

    this.currentUser.memberID = user.memberID;
    this.currentUser.email = user.email;
    this.currentUser.firstName = user.firstName;
    this.currentUser.lastName = user.lastName;
    this.currentUser.isSubscriber = user.isSubscriber;
    this.currentUser.isAdmin = user.isAdmin;
    this.currentUser.isDelegate = user.isDelegate;


    localStorage.setItem('loggedIn', 'true');
    localStorage.setItem('email', user.email);
    localStorage.setItem('memberID', user.memberID);
    localStorage.setItem('firstName', user.firstName);
    localStorage.setItem('lastName', user.lastName);
    localStorage.setItem('isAdmin', JSON.stringify(user.isAdmin));
    localStorage.setItem('isSubscriber', JSON.stringify(user.isSubscriber));
    localStorage.setItem('hasSubscribed', JSON.stringify(user.hasSubscribed));
    localStorage.setItem('isDelegate', JSON.stringify(user.isDelegate));

    // Notify observers about auth state changes
    this.loggedIn$.next(true);
    this.isAdmin$.next(this.currentUser.isAdmin);
    this.isAssistant$.next(this.currentUser.email == 'assistant@adsplat.me');
    console.debug('[AuthService] setUserInfo: loggedIn true, memberID:', this.currentUser.memberID);

  }

  clearUserInfo() {

    this.currentUser.email = "";
    this.currentUser.memberID = "";
    this.currentUser.firstName = "";
    this.currentUser.lastName = "";
    this.currentUser.isSubscriber = false;
    this.currentUser.hasSubscribed = false;
    this.currentUser.isAdmin = false;
    this.currentUser.isDelegate = false;

    localStorage.removeItem('token');
    localStorage.removeItem('loggedIn');
    localStorage.removeItem('memberID');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('isSubscriber');
    localStorage.removeItem('hasSubscribed');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isDelegate');

    // Notify observers that user is logged out
    this.loggedIn$.next(false);
    this.isAdmin$.next(false);
    this.isAssistant$.next(false);
    console.debug('[AuthService] clearUserInfo: logged out');
  }

}
